import React, { useEffect, useRef, useState } from 'react';
import SignaturePad from '../SignaturePad';

/**
 * SignatureField: the one signature or initials box of the app, for the
 * borrower, the lender and the witnesses alike. A dashed box with its label
 * above; it shows the saved image, or a pad to draw on when there is none,
 * with Save and Clear below.
 *
 * Props:
 *   label    {string}
 *   value    {string|null}  the saved image (data URL)
 *   onChange {function}     called with the image, or null on Clear
 *   kind     {string}       'signature' | 'initials'
 *   required {boolean}      a red star after the label
 *   disabled {boolean}      show the image only, no buttons
 *   actions  {node}         extra buttons after Save and Clear
 *   id       {string}       an id for the field, e.g. to scroll to it
 *   compact  {boolean}      smaller boxes, so several people fit one row
 *   locked   {boolean}      a saved signature someone else made: shown, not cleared
 *                           (a blank one is not protected and can be signed)
 */

const SIZES = {
  signature: { box: 'h-24 w-72 sm:w-80', width: 320, height: 96, save: 'Save Signature' },
  initials: { box: 'h-24 w-40', width: 160, height: 96, save: 'Save Initials' },
};
// Compact: the pad drawn at the box's size, so nothing is cut off.
const COMPACT_SIZES = {
  signature: { box: 'h-20 w-56', width: 224, height: 80, save: 'Save Signature' },
  initials: { box: 'h-20 w-32', width: 128, height: 80, save: 'Save Initials' },
};

// Whether a drawing has any ink: a pixel that is neither see-through nor
// (near) white. An empty pad saved as a picture is not a signature.
const hasInk = (canvasEl) => {
  const ctx = canvasEl?.getContext?.('2d');
  if (!ctx || !canvasEl.width || !canvasEl.height) return false;
  const { data } = ctx.getImageData(0, 0, canvasEl.width, canvasEl.height);
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] > 32 && (data[i] < 200 || data[i + 1] < 200 || data[i + 2] < 200)) return true;
  }
  return false;
};
// The same for a saved picture (data URL or link): resolves true / false.
const imageHasInk = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth || 1;
      c.height = img.naturalHeight || 1;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(hasInk(c));
    } catch {
      resolve(true); // cannot be read (another site): taken as signed
    }
  };
  img.onerror = () => resolve(false);
  img.src = src;
});

export const SIGNATURE_BUTTON = 'px-4 py-1.5 rounded text-sm transition disabled:opacity-50';
export const SIGNATURE_BUTTON_PLAIN = `${SIGNATURE_BUTTON} bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-300 dark:hover:bg-slate-500`;

const SignatureField = ({ label, value, onChange, kind = 'signature', required = false, disabled = false, actions = null, id, compact = false, locked = false }) => {
  const holder = useRef(null);
  const sizes = compact ? COMPACT_SIZES : SIZES;
  const size = sizes[kind] || sizes.signature;
  const canvas = () => holder.current?.querySelector('canvas');
  const [hint, setHint] = useState('');
  // A saved signature with no ink in it (an empty pad that was saved): not
  // signed. It is cleared, so what is missing is asked for again.
  const [blank, setBlank] = useState(false);
  useEffect(() => {
    let alive = true;
    setBlank(false);
    if (!value) return undefined;
    imageHasInk(value).then((ink) => {
      if (!alive || ink) return;
      setBlank(true);
      if (!disabled && typeof onChange === 'function') onChange(null);
    });
    return () => { alive = false; };
  }, [value]);
  const shown = value && !blank ? value : null;
  // Read only: the borrower's (disabled), or a real signature someone else made.
  const readOnly = disabled || (locked && Boolean(shown));

  const save = () => {
    const c = canvas();
    if (!c?.toDataURL) return;
    if (!hasInk(c)) { setHint('Draw first, then save.'); return; }
    setHint('');
    onChange(c.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvas();
    if (c?.getContext) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHint('');
    onChange(null);
  };

  return (
    <div className="flex flex-col items-center" id={id}>
      <p className="text-sm font-medium mb-2 text-gray-800 dark:text-slate-200">
        {label}{required && <span className="text-red-500"> *</span>}
      </p>
      <div ref={holder} className={`border-2 border-dashed border-gray-300 dark:border-slate-500 rounded-md bg-white dark:bg-slate-600 flex items-center justify-center overflow-hidden ${size.box}`}>
        {shown ? (
          <img src={shown} alt={label} className="h-full w-full object-contain" />
        ) : readOnly ? (
          <span className="text-xs text-gray-400 dark:text-slate-400">Not signed</span>
        ) : (
          <SignaturePad onSignatureChange={onChange} height={size.height} width={size.width} backgroundColor="rgba(255, 255, 255, 1)" hideInterface />
        )}
      </div>
      {!readOnly && (
        // Compact: smaller buttons ("Save", not "Save Initials"), so the
        // column stays as wide as its box.
        <div className={`flex flex-wrap justify-center mt-2 ${compact ? 'gap-1.5 [&>button]:!px-2.5 [&>button]:!py-1 [&>button]:!text-xs' : 'gap-2'}`}>
          {!shown && (
            <button type="button" onClick={save} className={`${SIGNATURE_BUTTON} bg-blue-600 text-white hover:bg-blue-700`}>{compact ? 'Save' : size.save}</button>
          )}
          <button type="button" onClick={clear} className={SIGNATURE_BUTTON_PLAIN}>Clear</button>
          {actions}
        </div>
      )}
      {hint && <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">{hint}</p>}
    </div>
  );
};

export default SignatureField;
